import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Handshake, Building2, HeartHandshake, Sprout } from "lucide-react";
import { Reveal, ScriptKicker, FilipinoSun } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPP_TYPES = [
  { id: "catering", label: "Catering Opportunity", icon: HeartHandshake, desc: "Recurring catering needs for your business or organization." },
  { id: "partnership", label: "Partnership", icon: Handshake, desc: "Long-term partnerships with venues, planners, and vendors." },
  { id: "event-collab", label: "Event Collaboration", icon: Building2, desc: "Co-host or collaborate on an event or pop-up." },
  { id: "community", label: "Community Involvement", icon: Sprout, desc: "Local fundraisers, cultural events, and community programs." },
];

export default function Opportunity() {
  usePageMeta({
    title: "Partnership & Catering Opportunities | Ateng K Sarap",
    description:
      "Partner with Ateng K Sarap — catering opportunities, event collaborations, and community involvement for San Diego's Filipino catering leader.",
    path: "/opportunity",
  });
  const [form, setForm] = useState({ name: "", email: "", organization: "", opportunity_type: "catering", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/opportunity`, form);
      toast.success("Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", organization: "", opportunity_type: "catering", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="opportunity-page" className="overflow-hidden">
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24 bg-background">
        <div className="absolute top-16 right-10 text-secondary/15 hidden lg:block"><FilipinoSun size={120} /></div>
        <div className="container-eds relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline flex items-center gap-3"><span className="w-8 h-px bg-secondary" /> Opportunity</div>
            <ScriptKicker className="block mt-3">Let's build something delicious</ScriptKicker>
            <h1 className="mt-2 font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-foreground leading-[0.98] tracking-tight">
              Let's grow Filipino <span className="italic text-secondary">flavor</span>
              <br />together.
            </h1>
          </div>
          <div className="lg:col-span-4 text-muted-foreground text-base lg:text-lg">
            Catering opportunities, partnerships, event collaborations, and
            community involvement — we'd love to hear from you.
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-card">
        <div className="container-eds">
          <Reveal>
            <div className="text-center mx-auto max-w-3xl">
              <div className="overline flex items-center justify-center gap-3"><span className="w-8 h-px bg-secondary" /> Ways to Work With Us <span className="w-8 h-px bg-secondary" /></div>
              <ScriptKicker className="block mt-3">Pick what fits</ScriptKicker>
              <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground leading-tight">
                Choose how we can <span className="italic text-primary">partner</span>.
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {OPP_TYPES.map((o, i) => {
              const offsets = ["", "md:translate-y-6", "", "md:translate-y-6"];
              return (
                <Reveal key={o.id} delay={i * 0.08} className={offsets[i]}>
                  <div className="bg-background border border-border rounded-3xl p-7 h-full group hover:bg-foreground hover:text-background transition-all duration-500">
                    <div className="w-14 h-14 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                      <o.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-display text-xl font-semibold">{o.label}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground group-hover:text-background/80">{o.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-background">
        <div className="container-eds max-w-3xl mx-auto">
          <Reveal>
            <div className="text-center mx-auto max-w-3xl">
              <div className="overline flex items-center justify-center gap-3"><span className="w-8 h-px bg-secondary" /> Tell Us About Your Idea <span className="w-8 h-px bg-secondary" /></div>
              <ScriptKicker className="block mt-3">We read every message</ScriptKicker>
              <h2 className="mt-2 font-display text-4xl lg:text-5xl font-medium text-foreground leading-tight">
                Reach out — let's <span className="italic text-secondary">talk</span>.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <form data-testid="opportunity-form" onSubmit={onSubmit} className="mt-12 bg-card border border-border p-8 lg:p-10 rounded-3xl space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your Name *">
                  <input data-testid="opp-name-input" name="name" value={form.name} onChange={onChange} required className="form-input" />
                </Field>
                <Field label="Email *">
                  <input data-testid="opp-email-input" name="email" type="email" value={form.email} onChange={onChange} required className="form-input" />
                </Field>
                <Field label="Organization">
                  <input data-testid="opp-org-input" name="organization" value={form.organization} onChange={onChange} className="form-input" />
                </Field>
                <Field label="Opportunity Type">
                  <select data-testid="opp-type-select" name="opportunity_type" value={form.opportunity_type} onChange={onChange} className="form-input">
                    {OPP_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Tell us more *">
                <textarea data-testid="opp-message-input" name="message" value={form.message} onChange={onChange} required rows={5} className="form-input resize-none" />
              </Field>
              <button type="submit" data-testid="opp-submit-btn" disabled={loading} className="btn-secondary w-full sm:w-auto disabled:opacity-60">
                {loading ? "Sending..." : "Submit Opportunity"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          padding: 0.85rem 1.1rem;
          font-size: 0.95rem;
          color: hsl(var(--foreground));
          border-radius: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-input:focus {
          border-color: hsl(var(--secondary));
          box-shadow: 0 0 0 3px hsl(var(--secondary) / 0.2);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold mb-2">{label}</span>
      {children}
    </label>
  );
}
